import { Col, FormGroup, Row, Text } from '@ksyos/design-system';
import type { AfwijkingOption } from '../../../../../../common/customControls/tooV1Afwijking';
import type { IDiagnosticReportGetOne } from '../../../../interfaces/diagnosticReportGetOne';
import { afwijkingReadOptions } from '../../../../../../common/customControls/tooV1Afwijking';
import { ConclusieDiagnoseOption } from '../../../../../../common/enums/conclusieDiagnoseOption';
import {
    Policies,
    TooV1DiagnosticReportCustomControlDataConclusieOption,
} from '../../../../../../common/validation/customControls/ophthalmology';
import {
    FormDivider,
    FormParagraph,
    HeaderDivider,
    Paragraph,
    StyledBorderAndPadding,
} from '../../../../elements/common';
import { FieldSet } from '../../../../elements/fieldset/FieldSet';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { Icd10SelectionsRead } from '../../utils/v1/Icd10/Icd10SelectionsRead';

export function TooV1DiagnosticReportReadCustomControlFields(props: { diagnosticReport: IDiagnosticReportGetOne }) {
    const customControlData = props.diagnosticReport.customControlData;
    const afwijking_in_vo = customControlData.afwijking_in_vo;
    const afwijking_in_ao = customControlData.afwijking_in_ao;
    const hasAfwijkingenAo =
        afwijking_in_ao.glasvocht || afwijking_in_ao.perifere_retina || afwijking_in_ao.papil || afwijking_in_ao.macula;
    const hasAfwijkingenVo =
        afwijking_in_vo.oogleden ||
        afwijking_in_vo.conjunctiva ||
        afwijking_in_vo.cornea ||
        afwijking_in_vo.vok ||
        afwijking_in_vo.iris ||
        afwijking_in_vo.lens;
    const optometrische_diagnose = customControlData.optometrische_diagnose || {};
    const conclusie_diagnose = customControlData.conclusie_diagnose;

    function isOptometrischeDiagnoseVisible() {
        return (
            optometrische_diagnose.droge_ogen ||
            optometrische_diagnose.cataract ||
            optometrische_diagnose.macula_degeneratie ||
            optometrische_diagnose.glaucoom ||
            optometrische_diagnose.retinopathie
        );
    }

    function showLabelsInOneLine(values: boolean[], labels: string[]) {
        return labels.filter((label, index) => !!values[index]).join(', ');
    }

    return (
        <>
            <HeaderDivider text="Bevindingen voorste oogsegment" />

            <HorizontalFormGroupRead>
                <FormLabel>Conclusie</FormLabel>
                <InputContainer>
                    <FormParagraph>{customControlData.conclusie_vo}</FormParagraph>
                </InputContainer>
            </HorizontalFormGroupRead>

            {shouldShowAfwijking(customControlData.conclusie_vo, hasAfwijkingenVo) && (
                <FieldSet>
                    {afwijking_in_vo.oogleden && (
                        <FormGroup label="Afwijking in oogleden">
                            {showAfwijkingOptions(afwijking_in_vo, 'oogleden')}
                        </FormGroup>
                    )}

                    {afwijking_in_vo.conjunctiva && (
                        <FormGroup label="Afwijking in conjunctiva">
                            {showAfwijkingOptions(afwijking_in_vo, 'conjunctiva')}
                        </FormGroup>
                    )}

                    {afwijking_in_vo.cornea && (
                        <FormGroup label="Afwijking in cornea">
                            {showAfwijkingOptions(afwijking_in_vo, 'cornea')}
                        </FormGroup>
                    )}

                    {afwijking_in_vo.vok && (
                        <FormGroup label="Afwijking in VOK">{showAfwijkingOptions(afwijking_in_vo, 'vok')}</FormGroup>
                    )}

                    {afwijking_in_vo.iris && (
                        <FormGroup label="Afwijking in iris">{showAfwijkingOptions(afwijking_in_vo, 'iris')}</FormGroup>
                    )}

                    {afwijking_in_vo.lens && (
                        <FormGroup label="Afwijking in lens">{showAfwijkingOptions(afwijking_in_vo, 'lens')}</FormGroup>
                    )}
                </FieldSet>
            )}

            <FormDivider />
            <HeaderDivider text="Bevindingen achterste oogsegment" />

            <HorizontalFormGroupRead>
                <FormLabel>Conclusie</FormLabel>
                <InputContainer>
                    <FormParagraph>{customControlData.conclusie_ao}</FormParagraph>
                </InputContainer>
            </HorizontalFormGroupRead>

            {shouldShowAfwijking(customControlData.conclusie_ao, hasAfwijkingenAo) && (
                <FieldSet>
                    {afwijking_in_ao.glasvocht && (
                        <FormGroup label="Afwijking in glasvocht">
                            {showAfwijkingOptions(afwijking_in_ao, 'glasvocht')}
                        </FormGroup>
                    )}

                    {afwijking_in_ao.perifere_retina && (
                        <FormGroup label="Afwijking in fundus">
                            {showAfwijkingOptions(afwijking_in_ao, 'perifere_retina')}
                        </FormGroup>
                    )}

                    {afwijking_in_ao.papil && (
                        <FormGroup label="Afwijking in papil">
                            {showAfwijkingOptions(afwijking_in_ao, 'papil')}
                        </FormGroup>
                    )}

                    {afwijking_in_ao.macula && (
                        <FormGroup label="Afwijking in macula">
                            {showAfwijkingOptions(afwijking_in_ao, 'macula')}
                        </FormGroup>
                    )}
                </FieldSet>
            )}

            <FormDivider />
            <HeaderDivider text="Optometrische diagnose" />
            <HorizontalFormGroupRead>
                {isOptometrischeDiagnoseVisible() && <FormLabel>Optometrische diagnose</FormLabel>}
                <InputContainer>
                    {conclusie_diagnose && conclusie_diagnose === ConclusieDiagnoseOption.nog_geen_diagnose && (
                        <FormParagraph bold>Nog Geen Diagnose</FormParagraph>
                    )}
                    {conclusie_diagnose && conclusie_diagnose === ConclusieDiagnoseOption.geen_afwijkingen && (
                        <FormParagraph bold> Geen Afwijkingen</FormParagraph>
                    )}
                    {optometrische_diagnose.cataract && <FormParagraph bold>Cataract</FormParagraph>}

                    {optometrische_diagnose.macula_degeneratie && (
                        <FormParagraph bold>
                            Macula degeneratie:{' '}
                            {showLabelsInOneLine(
                                [
                                    optometrische_diagnose.macula_degeneratie_nat,
                                    optometrische_diagnose.macula_degeneratie_droog,
                                    optometrische_diagnose.macula_degeneratie_suspect,
                                ],
                                ['Nat', 'Droog', 'Suspect']
                            )}
                        </FormParagraph>
                    )}

                    {optometrische_diagnose.retinopathie && (
                        <FormParagraph bold>
                            Retinopathie:{' '}
                            {showLabelsInOneLine(
                                [
                                    optometrische_diagnose.retinopathie_diabetische,
                                    optometrische_diagnose.retinopathie_overig,
                                ],
                                ['Diabetische retinopathie', 'Overig']
                            )}
                        </FormParagraph>
                    )}

                    {optometrische_diagnose.glaucoom && (
                        <FormParagraph bold>
                            Glaucoom:{' '}
                            {showLabelsInOneLine(
                                [
                                    optometrische_diagnose.glaucoom_ja,
                                    optometrische_diagnose.glaucoom_nee,
                                    optometrische_diagnose.glaucoom_suspect,
                                ],
                                ['Ja', 'Nee', 'Suspect']
                            )}
                        </FormParagraph>
                    )}

                    {optometrische_diagnose.droge_ogen && <FormParagraph bold>Droge Ogen</FormParagraph>}
                </InputContainer>
            </HorizontalFormGroupRead>
            <Icd10SelectionsRead
                diagnosis={props.diagnosticReport.diagnosis}
                hideTitle
                hideWorkingDiagnosisLabel={isOptometrischeDiagnoseVisible()}
                showWorkingDiagnosis
                thesaurusTermDate={props.diagnosticReport.dateSent}
            />

            <FormDivider />
            <HeaderDivider text="Advies" />

            {customControlData.geadviseerd_beleid && customControlData.geadviseerd_beleid !== Policies.anders && (
                <HorizontalFormGroupRead>
                    <FormLabel>Geadviseerd beleid</FormLabel>
                    <InputContainer>
                        <FormParagraph bold>{customControlData.geadviseerd_beleid}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {customControlData.geadviseerd_beleid_anders && (
                <HorizontalFormGroupRead>
                    <FormLabel>Geadviseerd beleid</FormLabel>
                    <InputContainer>
                        <FormParagraph bold>{customControlData.geadviseerd_beleid_anders}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {customControlData.urgentie && (
                <HorizontalFormGroupRead>
                    <FormLabel>Urgentie</FormLabel>
                    <InputContainer>
                        <FormParagraph bold>{customControlData.urgentie}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {customControlData.toelichting && (
                <HorizontalFormGroupRead>
                    <FormLabel>Toelichting</FormLabel>
                    <InputContainer>
                        <FormParagraph bold>{customControlData.toelichting}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}
        </>
    );
}

function showAfwijkingOptions(afwijking: any, name: AfwijkingOption) {
    const options = afwijkingReadOptions[name];

    return (
        afwijking[name] && (
            <Row columnGap={[2, 4, 6]}>
                <Col span={[12, 6, null, null, 4]}>
                    <StyledBorderAndPadding>
                        <Text
                            fontWeight="bold"
                            size="xs"
                        >
                            OD
                        </Text>
                        <Row columnGap={2}>
                            <Col span={12}>{afwijkingOptionsByEye(afwijking, name, options, 'Od')}</Col>
                        </Row>
                    </StyledBorderAndPadding>
                </Col>
                <Col span={[12, 6, null, null, 4]}>
                    <StyledBorderAndPadding>
                        <Text
                            fontWeight="bold"
                            size="xs"
                        >
                            OS
                        </Text>
                        <Row columnGap={2}>
                            <Col span={12}>{afwijkingOptionsByEye(afwijking, name, options, 'Os')}</Col>
                        </Row>
                    </StyledBorderAndPadding>
                </Col>
            </Row>
        )
    );
}

function afwijkingOptionsByEye(
    afwijking: any,
    name: string,
    options: { key: string; text?: string }[],
    eye: 'Od' | 'Os'
) {
    return (
        <>
            {options
                .filter((option) =>
                    option.key !== 'andersText'
                        ? afwijking[`${name}${eye}_${option.key}`]
                        : afwijking[`${name}AndersText`]
                )
                .map((option) => {
                    const key = `${eye}_${option.key}`;
                    if (option.key !== 'andersText') {
                        return (
                            <Paragraph
                                className="mb-0 font-weight-bold"
                                key={key}
                            >
                                {option.text}
                            </Paragraph>
                        );
                    } else if (afwijking[`${name}${eye}_andersCheck`]) {
                        return (
                            <Paragraph
                                className="mb-0 font-weight-bold"
                                key={key}
                            >
                                {afwijking[`${name}AndersText`]}
                            </Paragraph>
                        );
                    }
                })}
        </>
    );
}

function shouldShowAfwijking(conclusion: string | null, hasAfwijkingen: boolean) {
    return (
        conclusion === TooV1DiagnosticReportCustomControlDataConclusieOption.different ||
        (conclusion === TooV1DiagnosticReportCustomControlDataConclusieOption.normal && hasAfwijkingen)
    );
}
